import { Navigate } from 'react-router-dom';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import {
  FEATURE_OFF_REDIRECT,
  featureRouteDecision,
} from '@/lib/feature-route';

interface FeatureRedirectProps {
  feature: string;
  children: React.ReactNode;
  /** Where to send a tenant whose override (or plan) has this feature off. */
  to?: string;
}

/**
 * Route guard for a company feature. Unlike FeatureGate, which paints the
 * "isn't enabled" wall, this leaves the URL: a typed or bookmarked path
 * for a feature that was turned off goes back to the dashboard instead of
 * rendering the page (and its tab strip) anyway.
 *
 * While the check is in flight, show a spinner. Redirecting on the initial
 * `hasAccess: false` would bounce companies that have the feature.
 */
export function FeatureRedirect({
  feature,
  children,
  to = FEATURE_OFF_REDIRECT,
}: FeatureRedirectProps) {
  const { hasAccess, loading } = useFeatureAccess(feature);
  const decision = featureRouteDecision({ hasAccess, loading });

  if (decision === 'loading') {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (decision === 'redirect') {
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
}
